// src/pages/ApplicationSeason/index.tsx
import { useCallback, useState } from 'react';
import { Text, Flex, useToast, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { Season } from '@/model';
import { SeasonFilters } from './types';
import { SeasonTable } from './components/SeasonTable/SeasonTable';
import { SeasonFilterBox } from './components/SeasonFilterBox';
import { useSeasons, useDeleteSeasonMutation } from '@/services/queries/season';

export const ApplicationChampionshipSeason = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Season | undefined>(undefined);

  const filtersForm = useFiltersForm<SeasonFilters>({
    defaultValues: {
      championship_name: '',
      year: '',
      type: null,
      is_active: '',
      is_filtered: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({
    field: 'created_at',
    desc: true,
  });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useSeasons(params);
  const deleteMutation = useDeleteSeasonMutation();

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Season) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t(
            'pages.application_season.seasons.application_seasons_delete_success',
          ),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title:
            (resp && resp.message) ||
            t(
              'pages.application_season.seasons.application_seasons_delete_error',
            ),
        });
      },
    });
  };

  const onHandleEdit = (item: Season) => {
    navigate(`/application/championships/seasons/${item.uuid}/edit`);
  };

  const onHandleCreate = () => {
    navigate('/application/championships/seasons/create');
  };

  const onHandleDeleteClose = useCallback(() => {
    setIsConfirmModalOpen(false);
    setSelectedItem(undefined);
  }, []);

  return (
    <PageContent
      title={t(
        'pages.application_season.seasons.application_seasons_title',
      )}
      breadcrumbItems={breadcrumb}
    >
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justifyContent={{ md: 'space-between' }}
          gap={4}
        >
          <Text fontWeight="bold" color="brand.500">
            {t(
              'pages.application_season.seasons.application_seasons_label',
            )}
          </Text>

          <Button
            display="flex"
            alignItems="center"
            gap={2}
            onClick={onHandleCreate}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name="plus-circle" />
            {t(
              'pages.application_season.seasons.application_seasons_add_button',
            )}
          </Button>
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <SeasonFilterBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction="column" gap="deka" mt={8}>
          <SeasonTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) =>
              setPagination({ pageNumber, pageSize })
            }
            onEditClick={item => onHandleEdit(item)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={onHandleDeleteClose}
        onConfirm={onHandleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        title={t(
          'pages.application_season.seasons.application_seasons_delete_confirm_title',
        )}
        description={
          <Text>
            {t(
              'pages.application_season.seasons.application_seasons_delete_confirm_description_start',
            )}
            <Text as="span" fontWeight="bold">
              "{selectedItem?.championship?.name} {selectedItem?.year}"
            </Text>
            {t(
              'pages.application_season.seasons.application_seasons_delete_confirm_description_end',
            )}
          </Text>
        }
      />

      <CenteredSpinner isLoading={deleteMutation.isPending} />
    </PageContent>
  );
};
